import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { Stack, StackProps } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik, useField, useFormikContext } from "formik";
import React from "react";
import * as Yup from "yup";
import { AutoSave } from "../../commons/form-fields/AutoSave.js";
import { OrganisationMemberSelect } from "../organisations/OrganisationMemberSelect.js";
import {
  ProjectAssigneeSelect_UserDocument,
  ProjectAssigneeSelect_UserQuery,
  ProjectAssigneeSelect_UserQueryVariables,
  useProjectAssigneeSelectQuery,
  useProjectAssigneeSelect_AddProjectMemberMutation,
  useProjectAssigneeSelect_SetProjectAssigneeMutation,
} from "./ProjectAssigneeSelect.generated.js";

export interface FormValues {
  assigneeUserId: string | null;
}

interface Props {
  projectId: string;
  refetchQueries?: string[];
  readOnly?: boolean;
  autocompleteProps?: React.ComponentProps<typeof ProjectAssigneeField>;
  stackProps?: StackProps;
  autoSaveType?: React.ComponentProps<typeof AutoSave>["type"];
  autoSaveDebounceMs?: number;
  disabled?: boolean;
  onAssigneeChange?: (assigneeUserId: string | null) => void | Promise<void>;
  onAssigneeChanged?: (
    assigneeUserId: string | null,
    membershipId: string | null
  ) => void | Promise<void>;
}

export const ProjectAssigneeSelectFormWithAutoSave = ({
  projectId,
  refetchQueries,
  readOnly,
  autocompleteProps,
  stackProps,
  autoSaveType,
  autoSaveDebounceMs,
  disabled,
  onAssigneeChange,
  onAssigneeChanged,
}: Props) => {
  const { t } = useTranslate(["ProjectMembers", "Global"]);

  const client = useApolloClient();
  const query = useProjectAssigneeSelectQuery({
    client,
    variables: { projectId },
    fetchPolicy: "network-only",
  });
  const [addProjectMember] = useProjectAssigneeSelect_AddProjectMemberMutation({
    client,
  });
  const [setAssignee] = useProjectAssigneeSelect_SetProjectAssigneeMutation({
    client,
  });

  const assigneeMembershipId =
    getDataOrNull(query.data?.project)?.project?.assignee?.id ?? null;
  const projectMembers =
    getDataOrNull(query.data?.project)?.project?.internalStakeholders ?? [];
  const projectRoles = getDataOrNull(query.data?.project)?.project?.roles ?? [];
  const assigneeUserId = assigneeMembershipId
    ? (projectMembers.find(m => m.id === assigneeMembershipId)?.user?.id ??
      null)
    : null;
  const projectCouldBeUnassigned =
    query.data?.organisationSettings?.projectCouldBeUnassigned ?? false;
  const required = !projectCouldBeUnassigned;

  const initialValues = React.useMemo(
    (): FormValues => ({
      assigneeUserId,
    }),
    [assigneeUserId]
  );

  const validationSchema = Yup.object().shape({
    assigneeUserId: Yup.string()
      .label(t("Assignee", { ns: "ProjectMembers" }))
      .when([], {
        is: () => required,
        then: schema => schema.required(),
      })
      .nullable(),
  });

  async function addMemberToProject(userId: string) {
    const userResult = await client.query<
      ProjectAssigneeSelect_UserQuery,
      ProjectAssigneeSelect_UserQueryVariables
    >({
      query: ProjectAssigneeSelect_UserDocument,
      variables: { userId },
    });
    const user = userResult.data?.user;

    if (!user) {
      throw new Error("Cannot find user");
    }

    const roleIds =
      user.defaultProjectRoles.length > 0
        ? user.defaultProjectRoles.map(r => r.id)
        : projectRoles
            .filter(r => r.internalName === "PROJECT_MEMBER")
            .map(r => r.id);

    const result = await addProjectMember({
      variables: {
        projectId: projectId,
        userId: user.id,
        roleIds,
      },
    });
    const projectMembershipId =
      result.data?.addProjectMemberFromOrganisationMembers.project.internalStakeholders.find(
        m => m.user.id === user.id
      )?.id ?? null;

    if (!projectMembershipId) {
      throw new Error("Failed to add member to project");
    }

    return projectMembershipId;
  }

  const onSubmit = async ({
    assigneeUserId: newAssigneeUserId,
  }: FormValues) => {
    if (assigneeUserId === newAssigneeUserId) return;

    await onAssigneeChange?.(newAssigneeUserId);

    const projectMembershipId = !newAssigneeUserId
      ? null
      : (projectMembers.find(m => m.user.id === newAssigneeUserId)?.id ??
        (await addMemberToProject(newAssigneeUserId)));

    await setAssignee({
      variables: {
        projectId,
        membershipId: projectMembershipId,
      },
      refetchQueries,
      awaitRefetchQueries: true,
    });

    await onAssigneeChanged?.(newAssigneeUserId, projectMembershipId);
  };

  if (query.loading) return null;

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
    >
      {formikProps => (
        <Form>
          <Stack direction="column" spacing={0} {...stackProps}>
            <ProjectAssigneeField
              required={required}
              readOnly={readOnly}
              disabled={disabled ?? formikProps.isSubmitting}
              {...autocompleteProps}
            />
            <AutoSave
              type={autoSaveType}
              debounceMs={autoSaveDebounceMs}
              enableReinitialize
              initialValues={initialValues}
            />
          </Stack>
        </Form>
      )}
    </Formik>
  );
};

const FILTERS = { statuses: ["ACTIVATED" as const] };
function ProjectAssigneeField({
  ...props
}: Omit<
  React.ComponentProps<typeof OrganisationMemberSelect>,
  "inputLabel" | "value" | "onChange"
>) {
  const { t } = useTranslate(["ProjectMembers", "Global"]);
  const [{ value }, { error }, { setValue }] = useField("assigneeUserId");

  return (
    <OrganisationMemberSelect
      inputLabel={t("Assignee", { ns: "ProjectMembers" })}
      placeholder={t("Unassigned", { ns: "ProjectMembers" })}
      value={value}
      onChange={async value => {
        await setValue(value);
      }}
      error={error}
      filters={FILTERS}
      {...props}
    />
  );
}
